import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { catchError, Observable, switchMap, throwError } from 'rxjs';

/**
 * Auth Interceptor
 *
 * Handles:
 * 1. Adding Authorization header to requests
 * 2. Automatic refresh token when access token expires (401 response)
 * 3. Logout on refresh failure
 *
 * @param req - The HTTP request
 * @param next - The next handler
 */
export const authInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);
    const router = inject(Router);

    // Clone the request
    let newReq = req.clone();

    // Add Authorization header if we have a valid access token
    if (
        authService.accessToken &&
        !AuthUtils.isTokenExpired(authService.accessToken)
    ) {
        newReq = newReq.clone({
            headers: newReq.headers.set(
                'Authorization',
                `Bearer ${authService.accessToken}`
            ),
        });
    }

    // Handle the response
    return next(newReq).pipe(
        catchError((error) => {
            // Handle 401 Unauthorized responses
            if (error instanceof HttpErrorResponse && error.status === 401) {
                // Don't retry login/token endpoints
                const isAuthEndpoint = req.url.includes('/auth/token') ||
                                      req.url.includes('/auth/login') ||
                                      req.url.includes('/auth/refresh') ||
                                      req.url.includes('/auth/logout');

                // Don't retry if it's an incorrect password error
                const isIncorrectCredentials = error.error?.detail === 'Incorrect username or password';

                if (!isAuthEndpoint && !isIncorrectCredentials && authService.refreshToken) {
                    // Try to refresh the token
                    return authService.refreshAccessToken().pipe(
                        switchMap(() => {
                            // Retry the original request with the new token
                            const retryReq = req.clone({
                                headers: req.headers.set(
                                    'Authorization',
                                    `Bearer ${authService.accessToken}`
                                ),
                            });
                            return next(retryReq);
                        }),
                        catchError((refreshError) => {
                            // If refresh fails, logout and redirect to login
                            authService.signOut().subscribe({
                                complete: () => {
                                    // Redirect to login page with return URL
                                    const currentUrl = router.routerState.root.component ? router.url : '/';
                                    router.navigate(['/sign-in'], {
                                        queryParams: { returnUrl: currentUrl }
                                    });
                                }
                            });
                            return throwError(() => refreshError);
                        })
                    );
                } else if (!isAuthEndpoint) {
                    // If we don't have a refresh token or it's an auth endpoint, logout
                    authService.signOut().subscribe({
                        complete: () => {
                            // Redirect to login page
                            router.navigate(['/sign-in']);
                        }
                    });
                }
            }

            return throwError(() => error);
        })
    );
};
